import React, {useEffect, useState} from 'react'
import {Box, Button, Flex, Image, Stack, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {useGigya} from '../../context/gigya'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {FormattedMessage, useIntl} from 'react-intl'
import GigyaBasicFormController from '../../utils/gigya/lite-registration'
import PropTypes from 'prop-types'
import {BRAND_KEY, BRAND_SHORT_LABEL} from '../../brand'
import {buildCampaignTags, buildHmcString, hashSHA256} from '../../utils/utils'
import {useCustomPreferenceWithLocale} from '../../hooks/use-custom-preference'
import {CheckIcon} from '../icons'
import {useLocaleCountry} from '../../hooks/use-locale-custom'
import {useToast} from '../../hooks/use-toast'
import useGigyaSSR from '../../custom-hooks/use-gigya-ssr'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../custom-api/layers/default'

const NewsletterBanner = ({
    surveyAnswers,
    retrievedProducts,
    psp = false,
    title,
    subtitle,
    campaignTag,
    pspMarketingConsentMandatory,
    thankYouLabels,
}) => {
    const styles = useMultiStyleConfig('NewsletterBanner')
    const stylesLogin = useMultiStyleConfig('Login', {variant: 'newsletter'})
    const stylesPspNewsletter = useMultiStyleConfig('Login', {variant: 'pspNewsletter'})
    const [errorMessage, showErrorMessage] = useState(null)
    const [showThankyouSection, setShowThankyouSection] = useState(false)
    const [isCopied, setIsCopied] = useState(false)
    const {gigyaObject} = useGigya()
    const [isLoaded, setIsLoaded] = useState(false)
    const {formatMessage} = useIntl()
    const country = useLocaleCountry()
    const newsletterDiscount = useCustomPreferenceWithLocale('newsletterDiscount')
    const toast = useToast()
    const ssrGigyaMethods = useGigyaSSR()
    const {data: customer} = useCurrentCustomer()
    const {addLayer} = useGoogleTagManager()

    useEffect(() => {
        if (isLoaded || !gigyaObject) return
        gigyaObject.accounts.showScreenSet({
            screenSet: 'Default-LiteRegistration',
            startScreen: 'gigya-newsletter-screen',
            containerID: 'newsletter',
            deviceType: 'desktop',
            onBeforeScreenLoad: () => {},
            onAfterScreenLoad: (event) => {
                // Remove input labels and set placehoders
                const {sourceContainerID} = event
                const wrapper = document.getElementById(sourceContainerID)
                const fields = [
                    {name: 'profile.firstName'},
                    {name: 'profile.lastName'},
                    {name: 'profile.email', isEmail: true},
                ]
                fields.forEach(({name, isEmail}) => {
                    const inputSelector = isEmail
                        ? `input[data-gigya-name="${name}"]`
                        : `input[name="${name}"]`

                    const input = wrapper.querySelector(inputSelector)

                    if (input) {
                        const labelWrapper = wrapper.querySelector(`label[for="${input.id}"]`)

                        if (labelWrapper) {
                            const labelTextEl = labelWrapper.querySelector('.gigya-label-text')
                            const requiredEl = labelWrapper.querySelector('.gigya-required-display')

                            const labelText = labelTextEl?.textContent?.trim()

                            if (labelText) {
                                input.placeholder = labelText + '*'
                            }

                            if (labelTextEl) labelTextEl.style.display = 'none'
                            if (requiredEl) requiredEl.style.display = 'none'
                        }
                    }
                })
                // Prefill marketing consent checkbox
                const lang = window.gigya.thisScript.lang.originalLang
                const countryCode = window.gigya.thisScript.globalConf.countryCodeMapping[lang]
                const hasConsent = Object.values(event?.preferences || {})?.some(
                    (pref) =>
                        pref?.entitlements.includes(`${BRAND_KEY}${countryCode}`) &&
                        pref?.isConsentGranted === true,
                )
                if (hasConsent) {
                    const checkbox = document.querySelector(
                        countryCode === 'DE'
                            ? '#newsletter form div.gigya-layout-row input.gigya-input-checkbox'
                            : '#newsletter form div.gigya-layout-row div.gigya-custom-checkbox>input.gigya-input-checkbox',
                    )

                    setTimeout(() => {
                        checkbox.click()
                    }, 100)
                }

                setIsLoaded(true)
            },
            onBeforeValidation: () => {},
            onBeforeSubmit: (e) => {
                showErrorMessage(null)
                let proceed =
                    (psp && pspMarketingConsentMandatory === false) ||
                    (!psp && newsletterDiscount?.marketing_consent_mandatory === 'false') ||
                    GigyaBasicFormController.onBeforeSubmit(
                        e,
                        '.gigya-input-checkbox:not(.subscription-checkbox)',
                        true,
                    )
                if (!proceed)
                    showErrorMessage(
                        formatMessage({
                            id: 'guest_login.error.required_marketing',
                            defaultMessage: 'Please accept this to proceed.',
                        }),
                    )

                gigyaObject && gigyaObject.accounts && gigyaObject.accounts.logout()

                return proceed
            },
            onSubmit: (e) => {
                GigyaBasicFormController.onSubmit(
                    e,
                    null,
                    '.gigya-input-checkbox:not(.subscription-checkbox)',
                    buildHmcString(BRAND_KEY, retrievedProducts, surveyAnswers),
                    buildCampaignTags(
                        psp,
                        BRAND_KEY,
                        BRAND_SHORT_LABEL,
                        campaignTag,
                        retrievedProducts,
                        country,
                    ),
                )
            },
            onAfterSubmit: async (e) => {
                if (e?.response?.status === 'OK') {
                    await GigyaBasicFormController.onAfterSubmit(
                        e,
                        ssrGigyaMethods,
                        '.gigya-input-checkbox:not(.subscription-checkbox)',
                        false,
                    )
                    const guest_gigyaId = e?.response?.UID
                    const email = customer?.email || e?.profile?.email
                    const id = customer?.customerId || guest_gigyaId
                    if (id) {
                        await addLayer(
                            basic_gtm_event.bind(this, {
                                event: 'otherRegistrations',
                                eventCategory: 'sign_up',
                                eventAction: 'signUp-fast',
                                eventLabel: 'landingpage',
                                userId: id,
                                email,
                                emailSHA256: email ? await hashSHA256(email) : null,
                            }),
                        )
                    }
                    if (psp) {
                        toast({
                            title: formatMessage({
                                defaultMessage: 'Thank you for subscribing to our newsletter!',
                                id: 'user-newsletter-banner.success',
                            }),
                            status: 'success',
                        })
                    }
                    setShowThankyouSection(true)
                }
            },
            onError: () => {
                toast({
                    title: formatMessage({
                        defaultMessage: 'Something went wrong',
                        id: 'user-newsletter-banner.error',
                    }),
                    status: 'error',
                })
            },
            onFieldChanged: () => {},
        })
    }, [gigyaObject])

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text)
            setIsCopied(true)
        } catch (err) {
            console.error('Failed to copy text: ', err)
        }
    }

    return (
        <Flex {...styles?.container} visibility={isLoaded ? 'visible' : 'hidden'}>
            {psp ? (
                showThankyouSection ? (
                    <Flex {...styles?.pspThanksContentContainer}>
                        {thankYouLabels?.title && (
                            <Text {...styles?.pspThanksTitle}>{thankYouLabels?.title}</Text>
                        )}
                        {thankYouLabels?.subtitle && (
                            <Text {...styles?.pspThanksDescription}>
                                {thankYouLabels?.subtitle}
                            </Text>
                        )}
                        {thankYouLabels?.disclaimer && (
                            <Text {...styles?.pspThanksNotes}>{thankYouLabels?.disclaimer}</Text>
                        )}
                        {thankYouLabels?.date && (
                            <Text {...styles?.pspThanksNotes}>{thankYouLabels?.date}</Text>
                        )}
                    </Flex>
                ) : (
                    <Flex {...styles?.pspContentContainer}>
                        {title && <Text {...styles?.title}>{title}</Text>}
                        {subtitle && <Text {...styles?.description}>{subtitle}</Text>}

                        <Stack
                            id="psp-newsletter"
                            {...styles?.pspRegistrationFormStack}
                            __css={{
                                ...stylesPspNewsletter,
                            }}
                        >
                            <div id="newsletter"></div>
                            <Box {...styles.pspErrorMessage}>{errorMessage}</Box>
                        </Stack>
                    </Flex>
                )
            ) : (
                <>
                    <Image
                        {...styles?.imageElement}
                        alt="an image of a product"
                        src={
                            newsletterDiscount?.img_src ||
                            getAssetUrl('static/img/newsletterImage.png')
                        }
                    />
                    {showThankyouSection ? (
                        <Flex {...styles?.thanksContainer}>
                            <Text {...styles?.thanksTitle}>
                                <FormattedMessage
                                    defaultMessage="Thanks for your subscription"
                                    id="newsletter-banner.thankyou.title"
                                />
                            </Text>
                            <Text {...styles?.thanksDescription}>
                                <FormattedMessage
                                    defaultMessage="Here is your {discountValue}% discount code: <b>{discountCode}</b>"
                                    id="newsletter-banner.thankyou.description"
                                    values={{
                                        discountCode: newsletterDiscount?.discount_code,
                                        discountValue: newsletterDiscount?.discount_value,
                                        b: (boldString) => <b>{boldString}</b>,
                                    }}
                                />
                            </Text>
                            <Button
                                variant="primary"
                                {...styles?.thanksButton}
                                onClick={() => {
                                    handleCopy(newsletterDiscount?.discount_code)
                                }}
                            >
                                <FormattedMessage
                                    defaultMessage="Copy your discount code"
                                    id="newsletter-banner.thankyou.button"
                                />
                                {isCopied && <CheckIcon />}
                            </Button>
                            <Text {...styles?.thanksNotes}>
                                <FormattedMessage
                                    defaultMessage="You will receive your voucher and your result by email within 2 hours."
                                    id="newsletter-banner.thankyou.delivery"
                                />
                            </Text>
                            <Text {...styles?.thanksNotes}>
                                <FormattedMessage
                                    defaultMessage="*Valid for 30 days, exclusions may apply on some products."
                                    id="newsletter-banner.thankyou.validity"
                                />
                            </Text>
                        </Flex>
                    ) : (
                        <Flex {...styles?.contentContainer}>
                            <Text {...styles?.title}>
                                {title || (
                                    <FormattedMessage
                                        defaultMessage="Get your discount and save your results"
                                        id="newsletter-banner.title"
                                    />
                                )}
                            </Text>
                            <Text {...styles?.description}>
                                {subtitle || (
                                    <FormattedMessage
                                        defaultMessage="Register here to receive your results via email and unlock an exclusive 10% discount on the purchase of your ideal coffee maker."
                                        id="newsletter-banner.description"
                                    />
                                )}
                            </Text>

                            <Stack
                                {...styles?.registrationFormStack}
                                __css={{
                                    ...stylesLogin,
                                }}
                            >
                                <div id="newsletter"></div>
                                <Box {...styles.errorMessage}>{errorMessage}</Box>
                            </Stack>
                        </Flex>
                    )}
                </>
            )}
        </Flex>
    )
}

NewsletterBanner.propTypes = {
    retrievedProducts: PropTypes.array,
    surveyAnswers: PropTypes.object,
    psp: PropTypes.bool,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    campaignTag: PropTypes.string,
    pspMarketingConsentMandatory: PropTypes.bool,
    thankYouLabels: PropTypes.object,
}

export default NewsletterBanner
